import { SetError } from "../provider/ApiErrorProvider";

/**
 * Turns a failed BFF call into something the applicant can act on.
 *
 * The BFF answers `{ message, error }`, where `message` is a sentence written for
 * a person and `error` is either a short technical string or `{ message, code }`
 * from the Review Center. This used to read `data.error.errorMessage`, a field
 * nothing sends — so every failure produced the string "undefined", which the
 * error component then displayed as "An unexpected error occurred". A rate limit,
 * an expired session and an unreachable engine were all reported identically, and
 * none of them said the one thing the applicant needed to know.
 *
 * `message` is preferred because it is the field written to be read. The technical
 * `error` is deliberately not shown: it carries wallet-token and engine detail
 * that means nothing to an applicant and would leak implementation into the UI.
 */
export const handleApiError = (
  error: any,
  setError: SetError,
  retryAction: () => Promise<void>
) => {
  const errorResponse = error?.response;
  setError(messageFor(error, errorResponse), { retry: retryAction });
};

function messageFor(error: any, errorResponse: any): string {
  // No response at all: the request never landed. Retrying is the right advice,
  // and it is the one case where "something went wrong" is honestly all we know.
  if (!errorResponse) {
    return error?.code === "ECONNABORTED" ? "apierror.timeout" : "apierror.network";
  }

  const status = errorResponse.status;

  /*
   * Where we know what the status means, our own copy wins over the body's.
   *
   * Not every BFF `message` is advice. Some are written for a person — "We could
   * not find that code." — but the submission and status handlers send an
   * operation label, "An error occurred while submitting the application", which
   * tells the applicant only that they were doing the thing they know they were
   * doing. For a rate limit or a lost session there is a real remedy and it is
   * always the same one, so it is stated here rather than taken from the body.
   */
  if (status === 429) return "apierror.ratelimited";
  if (status === 409) return "apierror.sessionlost";
  if (status >= 500) return "apierror.server";

  /*
   * Everything else: the body's own sentence, which for the client-code and
   * applicant routes is the specific thing that went wrong, and for the rest is
   * at least the operation that failed. Returned verbatim rather than translated
   * — it is already English prose, and passing prose through i18next only echoes
   * it back.
   */
  const message = errorResponse?.data?.message;
  return typeof message === "string" && message.length > 0
    ? message
    : "apierror.unexpected";
}
